import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ISummary } from './types';
import { createSummaryThunk, getSummaryThunk, getRecordSummariesThunk } from './thunks';
import initialState from 'redux/store/initialState';
import { toast } from 'react-toastify';
import i18next from 'i18next';

// a wrapper function
const translate = (key: string): string => {
  try {
    // @ts-ignore - Tell TypeScript to ignore this line
    return i18next.t(key);
  } catch (e) {
    // Fallback if translation fails
    return key;
  }
};

const summariesSlice = createSlice({
  name: 'summaries',
  initialState: initialState.summaries,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setSelectedSummary(state, action: PayloadAction<ISummary | null>) {
      state.selectedSummary = action.payload;
    },
    addSummary(state, action: PayloadAction<ISummary>) {
      // Remove any existing summary with the same ID
      state.summaries = state.summaries.filter((doc) => doc.id !== action.payload.id);
      // Add new summary at the beginning since we sort by updated_at desc
      state.summaries.unshift(action.payload);
    },
    clearSummaries(state) {
      state.summaries = [];
      state.selectedSummary = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Summary
    builder.addCase(getSummaryThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getSummaryThunk.fulfilled, (state, action) => {
      state.loading = false;
      state.selectedSummary = action.payload;
    });
    builder.addCase(getSummaryThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = /*action.error.message ||*/ 'Failed to fetch summary';
      state.selectedSummary = null;
    });

    // Fetch Record Summaries
    builder.addCase(getRecordSummariesThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.summaries = [];
    });
    builder.addCase(getRecordSummariesThunk.fulfilled, (state, action) => {
      state.loading = false;
      state.summaries = action.payload;
    });
    builder.addCase(getRecordSummariesThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch summaries';
    });

    // Create Summary
    builder.addCase(createSummaryThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createSummaryThunk.fulfilled, (state, action) => {
      state.loading = false;
      state.selectedSummary = action.payload;
      // Remove any existing summary with the same ID
      state.summaries = state.summaries.filter((doc) => doc.id !== action.payload.id);
      // Add new summary at the beginning since we sort by updated_at desc
      state.summaries.unshift(action.payload);

      // Use our wrapper function to handle translation
      toast.success(translate('messages.summaryCreated') + '. ', { autoClose: 5000 });
    });
    builder.addCase(createSummaryThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to create summary';
    });
  },
});

export const actions = summariesSlice.actions;

export default summariesSlice.reducer;
